import { useState } from 'react';
import { LayoutGrid, Plus, Trash2 } from 'lucide-react';
import ToggleSwitch from '../BaseComponents/Toggle/ToggleSwitch';
import DeleteConfirmModal from '../BaseComponents/DeleteConfirmModal/DeleteConfirmModal';
import TravelIconPicker from '../../screens/CMS/Layout/HomepageSettings/components/TravelIconPicker';
import { isEmoji, isGlyph } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import { useToast } from '../../context/ToastContext';
import { MAX_MENU_ITEMS, MAX_SUB, customMenuKey, moveItem, newCustomItem } from './siteHeaderModel';
import { CUSTOM_ROW_ICON, MENU_BUTTON_ICONS } from './headerIcons';
import { customRows } from './headerPreviewModel';
import {
  BilingualInput, BuiltInTile, FooterLinkEditor, LinkField, RowActions, SectionCard,
  useFieldError, useHeaderEditor,
} from './editorControls';
import { WebsiteShows } from './DestinationsMenuSection';
import { menuCardId } from './MenuOrderSection';
import styles from './HeaderEditor.module.css';

const hasLabel = (menu) => Boolean(menu.label?.en || menu.label?.nl);
const menuName = (menu, index) => menu.label?.en || menu.label?.nl || `Custom menu ${index + 1}`;

function CustomRow({ item, index, count, base, onPatch, onMove, onRemove }) {
  const path = `${base}.items.${item.id}`;
  const labelError = useFieldError(`${path}.label`);
  const urlError = useFieldError(`${path}.url`);
  return (
    <div className={`${styles.itemCard} ${item.active ? '' : styles.itemCardOff} ${labelError || urlError ? styles.itemCardError : ''}`}>
      <div className={styles.itemTop}>
        <span className={styles.orderIndex}>{index + 1}</span>
        <TravelIconPicker
          kind="emoji"
          label="Row icon"
          value={isEmoji(item.icon) ? item.icon : ''}
          autoTile={(size) => <BuiltInTile icon={CUSTOM_ROW_ICON} size={size} />}
          autoLabel="Link icon"
          autoHint="A plain link icon"
          onChange={(icon) => onPatch({ icon })}
        />
        <div className={styles.itemTitle}>
          <strong>{item.label?.en || item.label?.nl || 'New row'}</strong>
          <span>{item.active ? (item.url ? `→ ${item.url}${item.newTab ? ' (new tab)' : ''}` : 'No link yet') : 'Hidden: switched off'}</span>
        </div>
        <span className={styles.toggleWrap} title={item.active ? 'Shown in the menu' : 'Hidden from the menu'}>
          <ToggleSwitch checked={item.active} onChange={() => onPatch({ active: !item.active })} />
        </span>
        <RowActions index={index} count={count} onMove={onMove} onRemove={onRemove} removeTitle="Remove row" />
      </div>
      <div className={styles.grid2}>
        <BilingualInput
          label="Label"
          value={item.label}
          required
          placeholder="e.g. Last minutes"
          errorPath={`${path}.label`}
          onChange={(label) => onPatch({ label })}
          compact
        />
        <BilingualInput
          label="Sub-label"
          value={item.sub}
          maxLength={MAX_SUB}
          placeholder="e.g. Leave within 6 weeks"
          onChange={(sub) => onPatch({ sub })}
          compact
        />
      </div>
      <div className={styles.grid2}>
        <LinkField
          value={item.url}
          required
          placeholder="/results?entry=last_minute"
          errorPath={`${path}.url`}
          newTab={item.newTab}
          onChange={(url) => onPatch({ url })}
          onNewTab={(newTab) => onPatch({ newTab })}
        />
      </div>
    </div>
  );
}

/**
 * One custom dropdown menu: a button with a label and a glyph, a list of links, and an optional
 * "see all" row. Deleting it removes it from the menu order too.
 */
export default function CustomMenuSection({ menu, index, set, errorCount }) {
  const { showToast } = useToast();
  const { lang } = useHeaderEditor();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const base = `menus.custom.${menu.id}`;
  const name = menuName(menu, index);
  const items = menu.items || [];
  const full = items.length >= MAX_MENU_ITEMS;

  // The custom menus are a list, so every change finds this one by id rather than by position.
  const updateMenu = (fn) =>
    set('menus.custom', (list) => (list || []).map((m) => (m.id === menu.id ? fn(m) : m)));
  const onPatch = (p) => updateMenu((m) => ({ ...m, ...p }));
  const updateItems = (fn) => updateMenu((m) => ({ ...m, items: fn(m.items || []) }));

  const addItem = () => {
    if (full) {
      showToast(`A menu holds at most ${MAX_MENU_ITEMS} rows`, 'error');
      return;
    }
    updateItems((list) => [...list, newCustomItem(list.map((it) => it.id))]);
    showToast(`Row added to ${name}. Give it a label and a link.`, 'success');
  };

  const removeItem = (i) => {
    const it = items[i];
    updateItems((list) => list.filter((_, x) => x !== i));
    showToast(`${it?.label?.en || it?.label?.nl || 'Row'} removed from ${name}`, 'success');
  };

  const deleteMenu = () => {
    set('', (sh) => ({
      ...sh,
      menus: { ...sh.menus, custom: sh.menus.custom.filter((m) => m.id !== menu.id) },
      menuOrder: sh.menuOrder.filter((k) => k !== customMenuKey(menu.id)),
    }));
    showToast(`${name} deleted. Save to remove it from the website.`, 'success');
  };

  return (
    <SectionCard
      id={menuCardId(customMenuKey(menu.id))}
      nested
      icon={LayoutGrid}
      tone="dark"
      title={hasLabel(menu) ? `Menu: ${name}` : `Custom menu ${index + 1}`}
      description="Your own dropdown of links."
      enabled={menu.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={menu.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
      errorCount={errorCount}
      previewTarget={`menu:${customMenuKey(menu.id)}`}
      headerExtra={
        <button type="button" className={styles.dangerBtn} onClick={() => setConfirmDelete(true)} title="Delete this menu">
          <Trash2 size={14} /> Delete
        </button>
      }
    >
      <div className={styles.menuHead}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Button icon</label>
          <TravelIconPicker
            kind="glyph"
            label="Button icon"
            tone="blue"
            value={isGlyph(menu.glyph) ? menu.glyph : ''}
            autoTile={(size) => <BuiltInTile icon={MENU_BUTTON_ICONS.custom} size={size} />}
            autoLabel="Grid"
            autoHint="A plain grid icon"
            onChange={(glyph) => onPatch({ glyph })}
          />
        </div>
        <BilingualInput
          label="Menu label"
          value={menu.label}
          required
          placeholder="e.g. Deals"
          errorPath={`${base}.label`}
          onChange={(label) => onPatch({ label })}
        />
      </div>

      <div className={styles.itemList}>
        {items.length === 0 && <p className={styles.emptyNote}>No rows yet. A menu without rows is left out of the bar.</p>}
        {items.map((it, i) => (
          <CustomRow
            key={it.id}
            item={it}
            index={i}
            count={items.length}
            base={base}
            onPatch={(p) => updateItems((list) => list.map((x) => (x.id === it.id ? { ...x, ...p } : x)))}
            onMove={(from, dir) => updateItems((list) => moveItem(list, from, dir))}
            onRemove={removeItem}
          />
        ))}
        <div className={styles.addRow}>
          <button type="button" className={styles.secondaryBtn} onClick={addItem} disabled={full}>
            <Plus size={14} /> Add row
          </button>
          <span className={styles.hint}>{items.length} / {MAX_MENU_ITEMS} rows</span>
        </div>
      </div>

      <WebsiteShows
        rows={customRows(menu, lang)}
        enabled={menu.enabled}
        emptyText="Nothing yet: the menu is left out of the bar"
      />

      <FooterLinkEditor
        value={menu.footerLink}
        errorPath={`${base}.footerLink.url`}
        onChange={(footerLink) => onPatch({ footerLink })}
      />

      <DeleteConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete menu"
        message={`Delete "${name}" and its ${items.length} row${items.length === 1 ? '' : 's'}? It leaves the website when you save.`}
        onConfirm={deleteMenu}
      />
    </SectionCard>
  );
}
